/**
 * Create Order — Redesign configuration
 *
 * Centralises the customer-facing preference model and shared constants used by
 * the redesigned Create Order workspace. Nothing here touches business logic —
 * these are purely the "describe the result you want" controls the customer sees.
 */

export type OrderFlow = 'hdr' | 'standard';

export interface PreferenceOption {
  value: string;
  label: string;
  /** Short helper copy — only shown for card-style groups (e.g. Editing Style). */
  description?: string;
  /** Heroicon (outline) name. */
  icon?: string;
  /** When true, selecting this option requires a reference image. */
  requiresReference?: boolean;
}

export interface PreferenceGroupConfig {
  key: string;
  label: string;
  icon: string;
  /** 'cards' renders richer option cards (icon + title + description). 'segmented' renders compact pills. */
  variant: 'cards' | 'segmented';
  options: PreferenceOption[];
}

/**
 * Editing Style — the headline creative choice. Rendered as premium cards.
 */
export const EDITING_STYLE_GROUP: PreferenceGroupConfig = {
  key: 'editingStyle',
  label: 'Editing Style',
  icon: 'SparklesIcon',
  variant: 'segmented',
  options: [
    { value: 'natural', label: 'Natural', description: 'True-to-life colour & light' },
    { value: 'bright_airy', label: 'Bright' },
    { value: 'luxury', label: 'Luxury' },
    { value: 'mls_standard', label: 'MLS' },
  ],
};

/**
 * The real-estate specific preference groups (segmented pills).
 */
export const DETAIL_PREFERENCE_GROUPS: PreferenceGroupConfig[] = [
  {
    key: 'sky',
    label: 'Sky',
    icon: 'CloudIcon',
    variant: 'segmented',
    options: [
      { value: 'original', label: 'Original' },
      { value: 'blue_sky', label: 'Blue Sky' },
      { value: 'sunset', label: 'Sunset' },
      { value: 'clear_sky', label: 'Clear Sky' },
      { value: 'cloudy_sky', label: 'Cloudy Sky' },
    ],
  },
  {
    key: 'grass',
    label: 'Grass',
    icon: 'SparklesIcon',
    variant: 'segmented',
    options: [
      { value: 'natural', label: 'Natural' },
      { value: 'green', label: 'Green' },
    ],
  },
  {
    key: 'windowView',
    label: 'Window View',
    icon: 'Square2StackIcon',
    variant: 'segmented',
    options: [
      { value: 'natural', label: 'Natural' },
      { value: 'window_pull', label: 'Window Pull' },
    ],
  },
  {
    key: 'perspective',
    label: 'Perspective',
    icon: 'ViewfinderCircleIcon',
    variant: 'segmented',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'keep', label: 'Keep Original' },
    ],
  },
  {
    key: 'tvScreens',
    label: 'TV Screens',
    icon: 'TvIcon',
    variant: 'segmented',
    options: [
      { value: 'keep', label: 'Keep Original' },
      { value: 'black', label: 'Black Screen' },
      { value: 'replace', label: 'Replace', requiresReference: true },
    ],
  },
];

export type PreferenceState = Record<string, string> & {
  minorCleanup: boolean;
};

/**
 * Defaults mirror the concept: Natural style, original sky, natural grass/window,
 * auto lights & perspective, TV screens kept, minor cleanup on.
 */
export const DEFAULT_PREFERENCES: PreferenceState = {
  editingStyle: 'natural',
  sky: 'original',
  grass: 'natural',
  windowView: 'natural',
  perspective: 'auto',
  tvScreens: 'keep',
  minorCleanup: true,
};

/** Human-readable labels for a preference value (for the summary + editor notes). */
export function preferenceLabel(groupKey: string, value: string): string {
  if (groupKey === 'editingStyle') {
    return EDITING_STYLE_GROUP.options.find((o) => o.value === value)?.label ?? value;
  }
  const group = DETAIL_PREFERENCE_GROUPS.find((g) => g.key === groupKey);
  return group?.options.find((o) => o.value === value)?.label ?? value;
}

/* ------------------------------------------------------------------ */
/* Pricing + delivery constants (kept identical to existing backend)  */
/* ------------------------------------------------------------------ */

/** Price per final HDR image — unchanged from the original HDR estimator. */
export const PRICE_PER_FINAL_HDR_IMAGE = 1.2;

/** Standard turnaround in hours. */
export const STANDARD_TURNAROUND_HOURS = 24;

/** Rush turnaround in hours. */
export const RUSH_TURNAROUND_HOURS = 12;

/** Rush surcharge as a fraction of the subtotal. */
export const RUSH_SURCHARGE_RATE = 0.4;

/** HDR bracket sizes the customer can pick from. */
export const HDR_BRACKET_SIZES = [2, 3, 4, 5, 6, 7] as const;

export const HDR_MIN_FILES = 3;
export const HDR_MAX_FILES = 300;

export const ACCEPTED_FORMATS = [
  '.arw', '.cr2', '.cr3', '.nef', '.dng', '.raf',
  '.jpg', '.jpeg', '.tiff', '.tif', '.png', '.webp',
];

export const MAX_FILE_SIZE_MB = 300;

/** Compute the rush fee for a given subtotal. */
export function computeRushFee(subtotal: number): number {
  if (subtotal <= 0) return 0;
  return Math.round(subtotal * RUSH_SURCHARGE_RATE * 100) / 100;
}
